/**
 * Lys-kompatibilitet og anbefalt innstilling for en gruppe planter i samme potte.
 *
 * Hardware-virkelighet: én LED-strip per potte, én PWM-kanal.
 * Alle planter i potten DELER samme intensitet og samme timer.
 * Vi kan derfor ikke skreddersy lys per plante — bare finne beste kompromiss.
 *
 * Konsepter:
 *  - Lys-familier: planter er gruppert etter dokumenterte DLI-krav (mol/m²/d).
 *  - Nabo-familier: tilstøtende familier er "kompatible" (mindre stress).
 *  - DLI ≈ PPFD (µmol/m²/s) × timer × 3600 / 1 000 000
 *    For vår LED-strip antar vi at intensitet 100 % på vanlig avstand
 *    gir omtrent 200 µmol/m²/s ved kanopi (typisk for en 12V phyto-strip).
 *    Dette er en ANTAGELSE til den er målt: alle DLI-funksjonene tar derfor
 *    PPFD som valgfri parameter, og appen sender inn brukerens kalibrerte
 *    verdi fra settings (`ppfdMaks`) — målt med PAR-meter/Photone, se LysSheet.
 */

import type { LysFamilieId, Plante, VekeEgnet, VannBehov } from './database.types';

/** Antatt PPFD ved 100 % intensitet, ca. 30 cm fra LED-strip. Konservativt anslag
 * — brukes som standard til brukeren kalibrerer med en faktisk måling. */
export const ANTATT_PPFD_MAX = 200;

/** Beregn DLI fra intensitet (%) og timer/dag, ved gitt PPFD på full styrke. */
export function beregnDli(intensitetProsent: number, timer: number, ppfdMax = ANTATT_PPFD_MAX): number {
  const ppfd = (intensitetProsent / 100) * ppfdMax;
  return (ppfd * timer * 3600) / 1_000_000;
}

/** Reverse: hvilken intensitet trengs for ønsket DLI ved gitt antall timer. */
export function intensitetForDli(dli: number, timer: number, ppfdMax = ANTATT_PPFD_MAX): number {
  if (timer <= 0) return 0;
  const ppfd = (dli * 1_000_000) / (timer * 3600);
  return Math.round((ppfd / ppfdMax) * 100);
}

/**
 * Sortering av familier på "lys-aksen" — fra lavest til høyest DLI-behov.
 * Brukes til å beregne avstand mellom familier (naboer = lite stress).
 * Rekkefølgen følger faktisk DLI-midtpunkt:
 *   mikrogrønt 6–12 < skygge-tolerante 8–14 < salat-blader 12–17
 *   < standard-urter 12–20 < solhungrige 18–30.
 */
const FAMILIE_AKSE: LysFamilieId[] = [
  'mikrogront',
  'skygge-tolerante',
  'salat-blader',
  'standard-urter',
  'solhungrige',
];

export function familieAvstand(a: LysFamilieId, b: LysFamilieId): number {
  return Math.abs(FAMILIE_AKSE.indexOf(a) - FAMILIE_AKSE.indexOf(b));
}

export type Kompatibilitet = 'perfekt' | 'god' | 'risikabel' | 'inkompatibel';

export interface KompatibilitetsRapport {
  niva: Kompatibilitet;
  melding: string;
  detaljer: string[];
}

/** Vurder hvor godt et sett planter passer sammen lys-messig. */
export function vurderLysKompatibilitet(planter: Plante[]): KompatibilitetsRapport {
  if (planter.length === 0) {
    return { niva: 'perfekt', melding: 'Ingen planter ennå', detaljer: [] };
  }
  if (planter.length === 1) {
    return { niva: 'perfekt', melding: 'Én plante — ingen kompromiss', detaljer: [] };
  }

  const familier = new Set(planter.map((p) => p.lys_familie));
  if (familier.size === 1) {
    return {
      niva: 'perfekt',
      melding: 'Alle planter i samme lys-familie',
      detaljer: [],
    };
  }

  // Finn maks avstand mellom noen to familier i settet
  const arr = [...familier];
  let maks = 0;
  for (let i = 0; i < arr.length; i++) {
    for (let j = i + 1; j < arr.length; j++) {
      maks = Math.max(maks, familieAvstand(arr[i]!, arr[j]!));
    }
  }

  const detaljer = forklarAvvik(planter);

  if (maks === 1) {
    return {
      niva: 'god',
      melding: 'Naboliggende lys-familier — fungerer med kompromiss',
      detaljer,
    };
  }
  if (maks === 2) {
    return {
      niva: 'risikabel',
      melding: 'Stor forskjell i lysbehov — noen planter vil ikke trives optimalt',
      detaljer,
    };
  }
  return {
    niva: 'inkompatibel',
    melding: 'Plantene har grunnleggende ulike lysbehov',
    detaljer,
  };
}

function forklarAvvik(planter: Plante[]): string[] {
  const linjer: string[] = [];
  const dliMin = Math.min(...planter.map((p) => p.dli_min ?? p.dli_optimal ?? 0).filter((x) => x > 0));
  const dliMax = Math.max(...planter.map((p) => p.dli_maks ?? p.dli_optimal ?? 0).filter((x) => x > 0));

  if (dliMin && dliMax && dliMax - dliMin >= 8) {
    linjer.push(`DLI-spenn: ${dliMin.toFixed(0)} – ${dliMax.toFixed(0)} mol/m²/d`);
  }

  const timerVerdier = planter.map((p) => p.timer_optimal ?? 0).filter((x) => x > 0);
  if (timerVerdier.length > 0) {
    const tMin = Math.min(...timerVerdier);
    const tMax = Math.max(...timerVerdier);
    if (tMax - tMin >= 4) {
      linjer.push(`Anbefalte timer: ${tMin} – ${tMax} t`);
    }
  }

  for (const p of planter) {
    const dliOpt = p.dli_optimal ?? 0;
    if (dliOpt >= 18) {
      linjer.push(`${p.navn} trenger MASSE lys (DLI ${dliOpt}) — gir kanskje ikke frukt med kompromiss-innstilling`);
    }
  }

  return linjer;
}

/**
 * Maks anbefalt fotoperiode (timer). Vi forlenger aldri forbi dette — det holder
 * et mørke-vindu på ≥6 t, som plantene trenger for respirasjon/døgnrytme.
 */
export const MAKS_FOTOPERIODE = 18;

// ============= SPIRING =============

/** De første dagene etter såing trenger frøene lite lys; varme og uttørking er risikoen. */
export const SPIRING_DAGER = 7;
export const SPIRING_INTENSITET = 50;

function dagStart(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Dag i spiringen (1 = sådd i dag), eller null når kassa ikke spirer. Kassa spirer når
 * ALLE plantene er sådd for under SPIRING_DAGER dager siden (den eldste avgjør).
 */
export function spiringsdag(plantetDatoer: (string | null | undefined)[], naa: number | Date = Date.now()): number | null {
  const tider = plantetDatoer.filter((s): s is string => !!s).map((s) => Date.parse(s)).filter(Number.isFinite);
  if (tider.length === 0) return null;
  const n = typeof naa === 'number' ? naa : naa.getTime();
  const dag = Math.round((dagStart(n) - dagStart(Math.min(...tider))) / 86_400_000) + 1;
  return dag >= 1 && dag <= SPIRING_DAGER ? dag : null;
}

/** Beregn anbefalt felles innstilling for et sett planter. */
export interface AnbefaltInnstilling {
  intensitet: number;
  timer: number;
  timer_on: string;
  timer_off: string;
  /** DLI som faktisk oppnås med anbefalt intensitet + timer. */
  dli: number;
  /** Plantenes ønskede DLI (median-optimum) — målet vi sikter mot. */
  dliMaal: number;
  /**
   * True når lystiden er forlenget UTOVER biologisk optimum fordi 100 %
   * intensitet ikke rakk opp til DLI-målet. Da kompenserer vi svakt lys med
   * tid — den eneste DLI-spaken som er igjen når intensiteten står i taket.
   */
  forlenget: boolean;
  /** Spiringsfase: intensiteten er satt ned til SPIRING_INTENSITET. */
  spiring?: boolean;
  /** Intensiteten som anbefales når spiringen er over. */
  vekstIntensitet?: number;
}

export function anbefaltInnstilling(planter: Plante[], ppfdMax = ANTATT_PPFD_MAX, spiring = false): AnbefaltInnstilling {
  const vekst = anbefaltVekst(planter, ppfdMax);
  if (!spiring || planter.length === 0) return vekst;
  const intensitet = Math.min(vekst.intensitet, SPIRING_INTENSITET);
  return {
    ...vekst,
    intensitet,
    dli: beregnDli(intensitet, vekst.timer, ppfdMax),
    spiring: true,
    vekstIntensitet: vekst.intensitet,
  };
}

function anbefaltVekst(planter: Plante[], ppfdMax: number): AnbefaltInnstilling {
  if (planter.length === 0) {
    return {
      intensitet: 70,
      timer: 14,
      timer_on: '07:00',
      timer_off: '21:00',
      dli: beregnDli(70, 14, ppfdMax),
      dliMaal: beregnDli(70, 14, ppfdMax),
      forlenget: false,
    };
  }

  // Median av optimale DLI- og timer-verdier — robust mot utliggere.
  const dliVerdier = planter
    .map((p) => p.dli_optimal ?? null)
    .filter((x): x is number => x !== null)
    .sort((a, b) => a - b);
  const targetDli = dliVerdier.length > 0 ? medianAv(dliVerdier) : 14;

  const timerVerdier = planter
    .map((p) => p.timer_optimal ?? null)
    .filter((x): x is number => x !== null)
    .sort((a, b) => a - b);
  const biologiskTimer = Math.round(timerVerdier.length > 0 ? medianAv(timerVerdier) : 14);

  // Hvor mye intensitet trengs for målet på biologisk dagslengde?
  const noedvendigIntensitet = intensitetForDli(targetDli, biologiskTimer, ppfdMax);

  let intensitet: number;
  let timer: number;
  let forlenget = false;

  if (noedvendigIntensitet <= 100) {
    // Lyset er sterkt nok — bruk biologisk optimum.
    intensitet = Math.max(20, noedvendigIntensitet);
    timer = biologiskTimer;
  } else {
    // Lyset rekker ikke opp på vanlig dagslengde → kjør 100 % og FORLENG dagen
    // (eneste DLI-spak igjen) opptil maks fotoperiode for å lukke gapet.
    intensitet = 100;
    const dliPerTime = beregnDli(100, 1, ppfdMax); // DLI tilført per time ved full styrke
    const timerForMaal = Math.ceil(targetDli / dliPerTime);
    timer = Math.min(MAKS_FOTOPERIODE, Math.max(biologiskTimer, timerForMaal));
    forlenget = timer > biologiskTimer;
  }

  // Symmetrisk dag rundt 14:00.
  const start = 14 - timer / 2;
  const stopp = 14 + timer / 2;
  const fmt = (h: number) => {
    const justert = ((Math.round(h * 60) % (24 * 60)) + 24 * 60) % (24 * 60);
    const t = Math.floor(justert / 60);
    const m = justert % 60;
    return `${pad(t)}:${pad(m)}`;
  };

  return {
    intensitet,
    timer,
    timer_on: fmt(start),
    timer_off: fmt(stopp),
    dli: beregnDli(intensitet, timer, ppfdMax),
    dliMaal: targetDli,
    forlenget,
  };
}

// ============= PER-PLANTE DLI-HELSE =============

export type PlanteDliNivaa = 'optimal' | 'akseptabel' | 'lavt' | 'hoyt';

export interface PlanteDliStatus {
  plante: Plante;
  status: PlanteDliNivaa;
  dliOpt: number;
}

/**
 * Hvor godt en gitt DLI treffer hver plantes dokumenterte behov. ÉN sannhet —
 * brukes både av lys-arkets «Plantene med denne innstillingen» og av oversiktens
 * varsel-feed (som flagger 'lavt'/'hoyt' på gjeldende lysplan).
 *   lavt        < 70 % av dli_min — reelt for lite, planten vil slite
 *   akseptabel  utenfor [min, maks], men innenfor ±30 % slingring
 *   hoyt        > 130 % av dli_maks — bortkastet strøm + stress-risiko
 * Planter uten DLI-data får 'optimal' (vi vet ikke bedre og maser ikke).
 */
export function vurderPlanteDli(planter: Plante[], dliEstimat: number): PlanteDliStatus[] {
  return planter.map((p) => {
    const dliMin = p.dli_min ?? p.dli_optimal ?? 0;
    const dliOpt = p.dli_optimal ?? 0;
    const dliMaks = p.dli_maks ?? p.dli_optimal ?? Infinity;
    let status: PlanteDliNivaa = 'optimal';
    if (dliEstimat < dliMin * 0.7) status = 'lavt';
    else if (dliEstimat < dliMin) status = 'akseptabel';
    else if (dliEstimat > dliMaks * 1.3) status = 'hoyt';
    else if (dliEstimat > dliMaks) status = 'akseptabel';
    return { plante: p, status, dliOpt };
  });
}

function medianAv(sorted: number[]): number {
  const n = sorted.length;
  if (n === 0) return 0;
  if (n % 2 === 1) return sorted[(n - 1) / 2]!;
  return (sorted[n / 2 - 1]! + sorted[n / 2]!) / 2;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

// ============= VANN-KOMPATIBILITET =============

const VANN_AKSE: VannBehov[] = ['lav', 'medium', 'hoy'];

export function vannAvstand(a: VannBehov, b: VannBehov): number {
  return Math.abs(VANN_AKSE.indexOf(a) - VANN_AKSE.indexOf(b));
}

export interface VannRapport {
  niva: 'ok' | 'forsiktig' | 'inkompatibel';
  melding: string;
  detaljer: string[];
}

export function vurderVannKompatibilitet(planter: Plante[]): VannRapport {
  if (planter.length <= 1) {
    return { niva: 'ok', melding: 'OK', detaljer: [] };
  }

  const detaljer: string[] = [];
  const ikkeAnbefalt = planter.filter((p) => p.veke_egnet === 'ikke_anbefalt');
  for (const p of ikkeAnbefalt) {
    detaljer.push(`${p.navn} er ikke anbefalt for veke-system (vil sannsynligvis råtne)`);
  }

  const forsiktig = planter.filter((p) => p.veke_egnet === 'forsiktig');
  for (const p of forsiktig) {
    detaljer.push(`${p.navn} foretrekker drenert jord — kan slite med konstant fukt`);
  }

  const vannSet = new Set(planter.map((p) => p.vann_behov));
  const arr = [...vannSet];
  let maksAvstand = 0;
  for (let i = 0; i < arr.length; i++) {
    for (let j = i + 1; j < arr.length; j++) {
      maksAvstand = Math.max(maksAvstand, vannAvstand(arr[i]!, arr[j]!));
    }
  }

  if (ikkeAnbefalt.length > 0 || maksAvstand >= 2) {
    return {
      niva: 'inkompatibel',
      melding: 'Plantene har for ulike vannbehov for samme veke-potte',
      detaljer,
    };
  }
  if (forsiktig.length > 0 || maksAvstand === 1) {
    return {
      niva: 'forsiktig',
      melding: 'Litt forskjell i vannbehov — fungerer som regel, men hold øye med fuktsensorer',
      detaljer,
    };
  }
  return { niva: 'ok', melding: 'Samme vannbehov — fungerer godt sammen', detaljer };
}

// ============= ENKLE UTILS =============

export function vekeEgnetTekst(v: VekeEgnet): string {
  switch (v) {
    case 'utmerket':
      return 'Trives i selvvannende potte';
    case 'bra':
      return 'Fungerer bra';
    case 'forsiktig':
      return 'Tåler det, men ikke ideelt';
    case 'ikke_anbefalt':
      return 'Vil sannsynligvis råtne';
  }
}

export function vekeEgnetFarge(v: VekeEgnet): string {
  switch (v) {
    case 'utmerket':
      return 'text-leaf';
    case 'bra':
      return 'text-leaf';
    case 'forsiktig':
      return 'text-sun';
    case 'ikke_anbefalt':
      return 'text-rose';
  }
}

export function vannBehovTekst(v: VannBehov): string {
  switch (v) {
    case 'hoy':
      return 'Mye vann';
    case 'medium':
      return 'Moderat';
    case 'lav':
      return 'Lite vann';
  }
}
